import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import Badge from './ui/Badge';
import { TONE_TEXT, type Tone } from './ui/tone';
import { getSlaState, type SlaState } from '../utils/slaState';

interface SlaBadgeProps {
  responseDueAt?: string | null;
  resolutionDueAt?: string | null;
  responseBreached?: boolean;
  resolutionBreached?: boolean;
  firstResponseAt?: string | null;
  /** Set by the backend SLA job once a clock passes its warning threshold. */
  responseWarningSentAt?: string | null;
  resolutionWarningSentAt?: string | null;
  status?: string;
  /**
   * List mode: renders a single quiet badge only when an SLA is at risk or
   * breached, and nothing while everything is on time (DESIGN_SYSTEM.md §2.3 —
   * colour is reserved for what needs attention).
   */
  compact?: boolean;
}

/** Only states that need attention get a colour; the rest stay neutral text. */
const STATE_TONE: Record<SlaState, Tone> = {
  onTime: 'neutral',
  approaching: 'warning',
  breached: 'danger',
  responded: 'neutral',
  closed: 'neutral',
};

const SEVERITY: Record<SlaState, number> = { breached: 3, approaching: 2, onTime: 1, responded: 0, closed: 0 };

function formatTimeRemaining(dueAt: string, t: TFunction): string {
  const diff = new Date(dueAt).getTime() - Date.now();
  if (diff <= 0) return '';

  const hours = Math.floor(diff / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return days === 1 ? t('sla.remainingDay', { count: days }) : t('sla.remainingDays', { count: days });
  }
  if (hours > 0) {
    return mins > 0
      ? t('sla.remainingHoursAndMinutes', { hours, minutes: mins })
      : t('sla.remainingHours', { hours });
  }
  return mins === 1 ? t('sla.remainingMinute', { count: mins }) : t('sla.remainingMinutes', { count: mins });
}

interface Row {
  key: 'response' | 'resolution';
  label: string;
  state: SlaState;
  timeLeft: string;
}

export default function SlaBadge({
  responseDueAt,
  resolutionDueAt,
  responseBreached,
  resolutionBreached,
  firstResponseAt,
  responseWarningSentAt,
  resolutionWarningSentAt,
  status,
  compact,
}: SlaBadgeProps) {
  const { t } = useTranslation();

  if (!responseDueAt && !resolutionDueAt) return null;

  const isClosed = status === 'completed' || status === 'closed';
  const hasResponded = !!firstResponseAt;

  const rows: Row[] = [];
  if (responseDueAt) {
    const state = getSlaState({ dueAt: responseDueAt, breached: responseBreached, responded: hasResponded, isClosed, warningSentAt: responseWarningSentAt });
    rows.push({ key: 'response', label: t('sla.response'), state, timeLeft: state === 'onTime' || state === 'approaching' ? formatTimeRemaining(responseDueAt, t) : '' });
  }
  if (resolutionDueAt) {
    const state = getSlaState({ dueAt: resolutionDueAt, breached: resolutionBreached, isClosed, warningSentAt: resolutionWarningSentAt });
    rows.push({ key: 'resolution', label: t('sla.resolution'), state, timeLeft: state === 'onTime' || state === 'approaching' ? formatTimeRemaining(resolutionDueAt, t) : '' });
  }

  const describe = (r: Row) => `${r.label}: ${t(`sla.${r.state}`)}${r.timeLeft ? ` — ${r.timeLeft}` : ''}`;

  if (compact) {
    const worst = rows.reduce((a, b) => (SEVERITY[b.state] > SEVERITY[a.state] ? b : a));
    if (worst.state !== 'breached' && worst.state !== 'approaching') return null;
    const breached = worst.state === 'breached';
    return (
      <Badge tone={breached ? 'danger' : 'warning'} title={rows.map(describe).join(' · ')}>
        <AlertTriangle size={12} aria-hidden="true" />
        {breached ? t('uxTickets.slaBreached') : t('uxTickets.slaAtRisk')}
      </Badge>
    );
  }

  return (
    <ul className="space-y-1.5">
      {rows.map((r) => {
        const tone = STATE_TONE[r.state];
        const Icon = r.state === 'breached' || r.state === 'approaching' ? AlertTriangle : r.state === 'onTime' ? Clock : CheckCircle2;
        return (
          <li key={r.key} className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-surface-600">{r.label}</span>
            <span className={`inline-flex items-center gap-1.5 text-end ${tone === 'neutral' ? 'text-surface-800' : `${TONE_TEXT[tone]} font-medium`}`}>
              <Icon size={14} className="shrink-0" aria-hidden="true" />
              <span>
                {t(`sla.${r.state}`)}
                {r.timeLeft && <span className="text-surface-600 font-normal"> · {r.timeLeft}</span>}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
